import {
  QueryJobFailed,
  QueryJobFileStatus,
  QueryJobInProgress,
  QueryJobSuccess,
} from '../types';

const MAX_TIMEOUT = 5 * 60 * 1000; // 5min

interface FetchQueryStateOptions {
  stateFileUrl: string;
  pollFrequency?: number;
  timeout?: number;
}

type FetchQueryStateData =
  | QueryJobInProgress
  | QueryJobFailed
  | QueryJobSuccess;

/**
 * Continues to poll job state until it has reached the completed state, error
 * state, or times out.
 *
 * @param options {FetchQueryStateOptions}
 */
export default function fetchQueryJobStatus(options: FetchQueryStateOptions) {
  const { stateFileUrl, pollFrequency = 1000, timeout = MAX_TIMEOUT } = options;

  return new Promise<string>((resolve, reject) => {
    const interval = setInterval(() => {
      fetch(stateFileUrl)
        .then((response) => {
          if (response.status === 503) {
            // retry 503s
            return {
              status: QueryJobFileStatus.IN_PROGRESS,
            };
          } else if (!response.ok) {
            // all other status codes are unacceptable
            throw new Error(
              `Unexpected status code received while polling query state (status=${response.status})`
            );
          }

          return response.json();
        })
        .then((data: FetchQueryStateData) => {
          switch (data.status) {
            case QueryJobFileStatus.IN_PROGRESS:
              // just return and wait for the next interval
              return;
            case QueryJobFileStatus.FAILED:
              throw new Error(data.error);
            case QueryJobFileStatus.COMPLETED:
              if (!data.url) {
                throw new Error('Query completed, but no url was included.');
              }

              clearInterval(interval);
              resolve(data.url);
              return;
            default:
              throw new Error(
                `Unexpected query status returned (queryStatus=${
                  (data as any)?.status
                })`
              );
          }
        })
        .catch(onError);
    }, pollFrequency);

    function onError(error: Error) {
      clearInterval(interval);
      reject(error);
    }

    setTimeout(() => {
      onError(new Error(`Fetching job state timed out after: "${timeout}ms"`));
    }, timeout);
  });
}
